import React from "react";

import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import CheckRoundedIcon from "@material-ui/icons/CheckRounded";
import HeadsetMicRoundedIcon from "@material-ui/icons/HeadsetMicRounded";
import EventAvailableRoundedIcon from "@material-ui/icons/EventAvailableRounded";
import TrendingUpRoundedIcon from "@material-ui/icons/TrendingUpRounded";
import ChatBubbleRoundedIcon from "@material-ui/icons/ChatBubbleRounded";

import { useSetup } from "../hooks/useSetup";

import {
  KoraxOnboardingShell,
  KoraxOnboardingContent,
  KoraxOnboardingFooter
} from "../../../components/KoraxUI/Onboarding";

const objectives = [
  {
    id: "vendas",
    icon: TrendingUpRoundedIcon,
    title: "Vender mais",
    description: "Qualificar leads, apresentar soluções e conduzir oportunidades."
  },
  {
    id: "atendimento",
    icon: ChatBubbleRoundedIcon,
    title: "Atender clientes",
    description: "Responder dúvidas, organizar demandas e agilizar o atendimento."
  },
  {
    id: "agendamento",
    icon: EventAvailableRoundedIcon,
    title: "Agendar serviços",
    description: "Identificar interesse, sugerir horários e organizar agendamentos."
  },
  {
    id: "suporte",
    icon: HeadsetMicRoundedIcon,
    title: "Dar suporte",
    description: "Resolver dúvidas frequentes e encaminhar casos mais específicos."
  }
];

const useStyles = makeStyles(theme => ({
  grid: {
    width: "100%",
    maxWidth: 820,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 14,

    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },

  card: {
    minHeight: 152,
    padding: 20,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    textAlign: "left",
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.025)"
        : "#FFFFFF",
    cursor: "pointer",
    transition: "border-color .18s ease, transform .18s ease, background .18s ease",
    fontFamily: "inherit",

    "&:hover": {
      transform: "translateY(-2px)",
      borderColor: "rgba(21,94,239,.45)"
    },

    "&:focus-visible": {
      outline: "3px solid rgba(21,94,239,.2)",
      outlineOffset: 2
    }
  },

  selected: {
    borderColor: "#155EEF",
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(145deg,rgba(21,94,239,.15),rgba(255,255,255,.025))"
        : "linear-gradient(145deg,rgba(21,94,239,.075),#FFFFFF)",
    boxShadow: "0 0 0 3px rgba(21,94,239,.08)"
  },

  top: {
    marginBottom: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14
  },

  icon: {
    width: 42,
    height: 42,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 12,
    color: "#155EEF",
    background: "rgba(21,94,239,.10)"
  },

  iconSvg: {
    fontSize: 22
  },

  check: {
    width: 23,
    height: 23,
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    border: `1px solid ${theme.palette.divider}`,
    color: "transparent",
    background: "transparent"
  },

  checkSelected: {
    borderColor: "#155EEF",
    color: "#FFFFFF",
    background: "#155EEF"
  },

  checkIcon: {
    fontSize: 16
  },

  title: {
    color: theme.palette.text.primary,
    fontSize: 15,
    fontWeight: 800,
    lineHeight: 1.3
  },

  description: {
    marginTop: 7,
    color: theme.palette.text.secondary,
    fontSize: 12.5,
    lineHeight: 1.55
  }
}));

export default function ChooseAIStep() {
  const classes = useStyles();

  const {
    ai,
    updateAI,
    nextStep,
    previousStep
  } = useSetup();

  return (
    <KoraxOnboardingShell
      step={4}
      totalSteps={7}
    >
      <KoraxOnboardingContent
        eyebrow="Prioridade da operação"
        title="Qual será o principal objetivo da sua IA?"
        description="Essa escolha ajuda a Korax a preparar a estrutura inicial do atendimento de acordo com a prioridade do seu negócio."
      >
        <div className={classes.grid}>
          {objectives.map(objective => {
            const selected = ai?.objective === objective.id;
            const Icon = objective.icon;

            return (
              <button
                key={objective.id}
                type="button"
                aria-pressed={selected}
                className={`${classes.card} ${
                  selected ? classes.selected : ""
                }`}
                onClick={() =>
                  updateAI({
                    objective: objective.id
                  })
                }
              >
                <div className={classes.top}>
                  <div className={classes.icon}>
                    <Icon className={classes.iconSvg} />
                  </div>

                  <div
                    className={`${classes.check} ${
                      selected ? classes.checkSelected : ""
                    }`}
                  >
                    <CheckRoundedIcon className={classes.checkIcon} />
                  </div>
                </div>

                <Typography className={classes.title}>
                  {objective.title}
                </Typography>

                <Typography className={classes.description}>
                  {objective.description}
                </Typography>
              </button>
            );
          })}
        </div>
      </KoraxOnboardingContent>

      <KoraxOnboardingFooter
        onBack={previousStep}
        onNext={nextStep}
        disableNext={!ai?.objective}
      />
    </KoraxOnboardingShell>
  );
}
